<template>
  <div class="social-fab-container" :class="{ visible: showButton }">
    <div class="overlay" v-if="isOpen" @click="closeMenu"></div>

    <transition-group name="fade" tag="div">
      <template v-if="isOpen">
        <a
          v-for="(social, index) in socialLinks"
          :key="social.name"
          :href="social.url"
          class="social-icon"
          :style="{ bottom: `${90 + index * 60}px` }"
          target="_blank"
          rel="noopener noreferrer"
        >
          <component :is="social.icon" class="icon" />
        </a>
      </template>
    </transition-group>

    <button
      class="fab-button"
      @click="toggleMenu"
      :aria-label="isOpen ? 'Close' : 'Open Social Menu'"
    >
      <span class="cross" :class="{ open: isOpen }"></span>
    </button>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useStore } from "vuex";
import {
  Linkedin,
  Twitter,
  Instagram,
  Facebook,
  MessageCircle,
} from "lucide-vue-next"; // you can customize this list based on your UI

const isOpen = ref(false);
const showButton = ref(false);
const store = useStore();

const toggleMenu = () => {
  isOpen.value = !isOpen.value;
};

const closeMenu = () => {
  isOpen.value = false;
};

const handleScroll = () => {
  showButton.value = window.scrollY > 100;
};

const handleClickOutside = (e) => {
  const fab = document.querySelector(".social-fab-container");
  if (fab && !fab.contains(e.target)) {
    closeMenu();
  }
};

onMounted(() => {
  window.addEventListener("scroll", handleScroll);
  document.addEventListener("click", handleClickOutside);
  handleScroll();
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", handleScroll);
  document.removeEventListener("click", handleClickOutside);
});

// Computed social links from store
const socialLinks = computed(() => {
  return [
    {
      name: "LinkedIn",
      url: store.state.linkdin,
      icon: Linkedin,
    },
    {
      name: "Twitter",
      url: store.state.twitter,
      icon: Twitter,
    },
    {
      name: "Instagram",
      url: store.state.instagram,
      icon: Instagram,
    },
    {
      name: "Facebook",
      url: store.state.facebook,
      icon: Facebook,
    },
    {
      name: "WhatsApp",
      url: `https://wa.me/${store.state.phone}`,
      icon: MessageCircle,
    },
  ].filter((link) => link.url); // only show those that have a URL
});
</script>

<style scoped>
/* same as before */
.social-fab-container {
  position: fixed;
  bottom: 20px;
  left: 20px;
  z-index: 1000;
  transition: opacity 0.5s ease, transform 0.5s ease;
  opacity: 0;
  transform: translateY(30px);
  pointer-events: none;
}

.social-fab-container.visible {
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
}

.fab-button {
  width: 60px;
  height: 60px;
  background-color: #2b5c3f;
  border: none;
  border-radius: 50%;
  color: white;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease;
  z-index: 1001;
}

.cross {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 24px;
  height: 2px;
  background: white;
  transform: translate(-50%, -50%);
  transition: background 0.3s ease;
}

.cross::before,
.cross::after {
  content: "";
  position: absolute;
  width: 24px;
  height: 2px;
  background: white;
  transition: transform 0.3s ease;
}

.cross::before {
  top: -8px;
}

.cross::after {
  top: 8px;
}

.cross.open {
  background: transparent;
}

.cross.open::before {
  transform: rotate(45deg) translate(5px, 5px);
}

.cross.open::after {
  transform: rotate(-45deg) translate(5px, -5px);
}

.social-icon {
  position: absolute;
  left: 20px;
  background-color: #2b5c3f;
  border-radius: 50%;
  padding: 12px;
  color: white;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
  transition: opacity 0.4s ease, transform 0.4s ease;
  z-index: 1001;
}

.icon {
  width: 24px;
  height: 24px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s ease, transform 0.4s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.2);
}
</style>
